<template>
  <div class="sv sv011">
    <div class="container">
      <breadcrumb-group :items="breadcrumbItems"/>

      <contents-header class="-green" src="sv011/main.jpg">
        <p class="heading1">いまさら聞けない！ローンの基礎知識</p>
        <h1 class="heading2">ローンって<br>借りすぎないかな？</h1>
      </contents-header>
    </div>

    <div class="container">
      <contents-intro
        class="-green _pt-sm-50"
        icon-left="sv011/avatar-a.gif"
        name-left="Aさん"
        icon-right="sv011/avatar-b.gif"
        name-right="Bさん"
        txt-relation="同僚の2人">
        最近ローンを初めて利用したBさん。でも、「つい借りすぎてしまうんじゃ？」と不安に思っているようで・・・。<br>
        そんなBさんを見て、Aさんがアドバイスをしてくれます。
      </contents-intro>
    </div>

    <hr class="-pcnone _mb-sm-60">

    <div class="container">

      <heading-two class="_mt-md-110 _mt-sm-60">借り入れには限度額があるから、借りすぎる心配をしなくてもいい！</heading-two>

      <div class="contents-box01">
        <talk-group class="_mr-md-20">
          <talk-box src="sv011/avatar-b.gif">手元にお金がないとき便利でついつい借りちゃうけど、借入金額が雪だるま式に増えてしまいそうで不安…</talk-box>
          <talk-box src="sv011/avatar-a.gif">キャッシングやローンには、お金を借りすぎないようにする仕組みがあるから大丈夫だよ</talk-box>
          <talk-box src="sv011/avatar-a.gif">キャッシングやローンを何度も利用する場合でも、借り入れの限度額を超えた金額は借りることができないから、いつの間にかたくさん借りていた！なんてことはないよ</talk-box>
        </talk-group>
        <contents-card class="-green _mb-sm-30 _ml-md-20">
          <div class="card-header">
            <icon-box src="light-green" class="-md _mb-sm-10"></icon-box>
            <h3 class="heading -green">借り入れの限度額とは？</h3>
          </div>
          <div class="card-body">
            <div class="card-groups">
              <div class="card-box">
                <list-group class="-normal">
                  <list-item class="-normal">１社から借りられる金額の上限のことです。</list-item>
                  <list-item class="-normal">借り入れの限度額は利用者の収入や、過去のキャッシング・ローンの利用状況などをもとに審査をし、貸金業者が決めています。</list-item>
                </list-group>
              </div>
            </div>
          </div>
        </contents-card>
      </div>

      <talk-group>
        <talk-box src="sv011/avatar-b.gif">それなら安心ね</talk-box>
        <talk-box src="sv011/avatar-a.gif">
          借り入れの限度額は『利用可能額』や『枠』などと呼ばれているよ。自分がいくら借りられるのかを診断できるシミュレーションもあるから、お金を借りるときにはチェックしておくといいよ<br>
          <btn-text routerName="sv021" class="-next">シミュレーションについて詳しくはこちら</btn-text>
        </talk-box>
      </talk-group>
      <list-group class="-note">
        <list-item class="-note">au PAY スマートローンでは、『ご利用限度額』と表示されます。</list-item>
      </list-group>

      <heading-two class="_mt-md-110 _mt-sm-60">利用者は法律で定められた「総量規制」で守られている！</heading-two>

      <talk-group class="_mb-md-60 _mb-sm-40">
        <talk-box src="sv011/avatar-b.gif">でも、貸金業者はお金を貸すのがお仕事でしょ。借り入れの限度額があっても、何社か契約すれば、どんどん借入金額が増えちゃいそう…</talk-box>
        <talk-box src="sv011/avatar-a.gif">そうならないように『総量規制』があるんだよ、知ってる？</talk-box>
        <talk-box src="sv011/avatar-b.gif">知らないなあ…</talk-box>
        <talk-box src="sv011/avatar-a.gif">１社から借り入れできる金額の上限（借り入れの限度額）の他に、全ての会社（貸金業者）から借り入れできる金額にも上限があるんだよ。その上限が『総量規制』なんだよ</talk-box>
      </talk-group>

      <div class="contents-box02">
        <contents-card class="-green _mb-sm-30 _mr-md-10">
          <div class="card-header">
            <icon-box src="light-green" class="-md _mb-sm-10"></icon-box>
            <h3 class="heading -green">総量規制とは</h3>
          </div>
          <div class="card-body">
            <div class="card-groups">
              <div class="card-box">
                全ての貸金業者から借り入れできる金額には制限があります。<br>
                原則貸金業者からの借入金額が年収の3分の1を超えると、新たにお金を借りることができなくなります。
                <div class="card-image _my-sm-20 _mt-md-25 _mb-md-15">
                  <img src="@/assets/images/sv/sv011/img01.gif" class="img-fluid" alt="">
                </div>
                <div class="txt01">
                  金融庁「貸金業法のキホン」<br>
                  <a href="https://www.fsa.go.jp/policy/kashikin/kihon.html" target="_blank">（https://www.fsa.go.jp/policy/kashikin/kihon.html)</a>
                  をもとに作成
                </div>
              </div>
            </div>
          </div>
        </contents-card>
        <contents-card class="-green _mb-sm-30 _ml-md-10">
          <div class="card-header">
            <icon-box src="light-green" class="-md _mb-sm-10"></icon-box>
            <h3 class="heading -green">総量規制があるから借りすぎない！</h3>
          </div>
          <div class="card-body _py-sm-10 _pt-md-20 _pb-md-20">
            <div class="card-groups">
              <div class="card-box">
                <p class="txt02">利用者が年収の3分の1を超えてお金を借りようとしても借りられない。
                  <span class="txt03">「借りすぎない！」</span></p>
                <p class="txt02 -border">貸金業者が年収の3分の1を超えてお金を貸そうとしても貸せない。
                  <span class="txt03">「貸しすぎない！」</span></p>
                <p class="txt02">すべての貸金業者からの借入の合計額で判断されるから、
                  <span class="txt03">「借りすぎ・貸しすぎを防げる！」</span></p>
              </div>
            </div>
          </div>
        </contents-card>
      </div>

      <talk-group>
        <talk-box src="sv011/avatar-b.gif">ということは、今借りているA社の借り入れが限度額いっぱいになって、新しいB社で契約しようと思っても…</talk-box>
        <talk-box src="sv011/avatar-a.gif">そう！借入金額の総額を合計して年収の3分の1までしか借りられないから、借りすぎてしまうことがないんだよ</talk-box>
      </talk-group>

      <heading-two class="_mt-md-110 _mt-sm-60">貸金業者は情報を共有して、お金の「借りすぎ・貸しすぎ」を防いでいる！</heading-two>

      <talk-group class="_mb-md-60 _mb-sm-40">
        <talk-box src="sv011/avatar-b.gif">でもさ…、貸金業者はたくさんあるから、黙っていればいくらでも借りられそう…</talk-box>
        <talk-box src="sv011/avatar-a.gif">それはできないんだ。どの会社も借り入れの契約などの際には、『指定信用情報機関』という所に利用者の情報を提供する義務があって、各社は登録された情報をもとに判断しているんだよ。だから、Bさんが借り入れのことを黙っていても、全部わかってしまうんだ</talk-box>
      </talk-group>

      <contents-card class="-green _mb-sm-30">
        <div class="card-header">
          <icon-box src="light-green" class="-md _mb-sm-10"></icon-box>
          <h3 class="heading -green">利用者情報の共有の流れ</h3>
        </div>
        <div class="card-body01">
          <div class="card-groups">
            <div class="card-box order2">
              <div class="card-image">
                <img src="@/assets/images/sv/sv011/img02.gif" class="img-fluid" alt="">
              </div>
            </div>
            <div class="card-box _pl-md-60 order1 _ml-md-20">
              <div class="card-body">
                <ul class="-border">
                  <li class="list circle-box"><span class="circle -orange _mt-md-3">1</span></li>
                  <li class="list txt01">契約申し込み</li>
                  <li class="list circle-box"><span class="circle -green _mt-md-3">2</span></li>
                  <li class="list txt01">信用情報の照会</li>
                  <li class="list circle-box"><span class="circle -blue _mt-md-3">3</span></li>
                  <li class="list txt01">信用情報の提供</li>
                  <li class="list circle-box"><span class="circle -green _mt-md-3">4</span></li>
                  <li class="list txt01">信用供与</li>
                  <li class="list circle-box"><span class="circle -green _mt-md-3">5</span></li>
                  <li class="list txt01">信用供与結果の登録</li>
                  <li class="list circle-box"><span class="circle -orange _mt-md-3">6</span></li>
                  <li class="list txt01">信用情報の確認</li>
                  <li class="list circle-box"><span class="circle -blue _mt-md-3">7</span></li>
                  <li class="list txt01">信用情報の開示</li>
                </ul>
                  <card-header>
                  <h2 class="heading">信用情報機関に登録されている情報の一例</h2>
                  </card-header>
                  <list-group class="-normal _mb-md-50">
                  <list-item class="-normal">どの利用者がどのくらいのお金を借りているのか。</list-item>
                  <list-item class="-normal">借りたお金を計画通り返済できているのか。</list-item>
                </list-group>
              </div>
            </div>
          </div>
        </div>
      </contents-card>

      <talk-group>
        <talk-box src="sv011/avatar-b.gif">お金を借りすぎないように、法律でしっかり守られているんだね！</talk-box>
        <talk-box src="sv011/avatar-a.gif">そう。過度な心配をすることないので、計画的に活用するといいよ。</talk-box>
      </talk-group>

      <small>※au PAY スマートローンのご契約・ご利用には審査がございます。審査状況によりご希望に添えない場合がございますので、あらかじめご了承ください。</small>

      <div class="container">
        <btngroup-single class="-margin-lg">
          <btn-main class="-normal -lg" routerName="sv002">au PAY スマートローンの特徴について</btn-main>
        </btngroup-single>
      </div>
      <cv-box class="-cv _mb-md-120 _mb-sm-60"/>
    </div>
    <hr class="_mb-120 _mb-sm-60" />
    <div class="container">
      <heading-two>おすすめ記事</heading-two>
      <card-groups class="-image-top-list">
        <card-box router-name="sv012" class="-image-top">
          <card-header>
            <card-image class="-green"><img src="@/assets/images/sv/sv012/thumb.jpg" class="img-fluid" alt=""></card-image>
            <h3 class="heading">お金を借りたいけど、周りの人に知られない？</h3>
          </card-header>
          <card-body>「ローンのこと気になっていたけど、家族や恋人に知られないかな？」にお答えします。</card-body>
        </card-box>
        <card-box router-name="sv009" class="-image-top">
          <card-header>
            <card-image class="-green"><img src="@/assets/images/sv/sv009/thumb.jpg" class="img-fluid" alt=""></card-image>
            <h3 class="heading">ローンにおける金利の仕組みとは？</h3>
          </card-header>
          <card-body>「金利・利息・実質年率ってなんだろう？」いまさら聞けないローンのキホンについて！</card-body>
        </card-box>
        <card-box router-name="sv010" class="-image-top">
          <card-header>
            <card-image class="-green"><img src="@/assets/images/sv/sv010/thumb.jpg" class="img-fluid" alt=""></card-image>
            <h3 class="heading">ローンの返済方法やルールとは？</h3>
          </card-header>
          <card-body>「お金を借りたらどうやって返すの…？」au PAY スマートローンの返済方法についてはこちら。</card-body>
        </card-box>
      </card-groups>

    </div><!-- container -->
  </div>
</template>

<script>

import ContentsHeader from '../components/contents/ContentsHeader'
import ContentsIntro from '../components/contents/ContentsIntro'
import HeadingTwo from '../components/heading/HeadingTwo'
import TalkGroup from '../components/contents/TalkGroup'
import TalkBox from '../components/contents/TalkBox'
import ContentsCard from '../components/contents/ContentsCard'
import IconBox from '../components/icons/icon-box'
import ListGroup from '../components/list/ListGroup'
import ListItem from '../components/list/ListItem'
import BtngroupSingle from '../components/button/BtngroupSingle'
import BtnMain from '../components/button/BtnMain'
import CvBox from '../components/box/CvBox'
import CardGroups from '../components/card/CardGroups'
import CardBox from '../components/card/CardBox'
import CardHeader from '../components/card/CardHeader'
import CardImage from '../components/card/CardImage'
import CardBody from '../components/card/CardBody'
import BtnText from '../components/button/BtnText'

export default {
  name: 'Sv-011',
  metaInfo: {
    title: 'ローンって借りすぎないかな？｜いまさら聞けない！ローンの基礎知識',
    meta: [
      {name: 'description', content: '意外と知らないローンの基礎知識！借入限度額や総量規制について初心者でも分かりやすくご説明します。au PAY スマートローンはau IDをお持ちのお客様のための便利なカードレスローンサービスです。お申込みは、スマートフォン・パソコンでかんたんWEB完結！24時間お申込み可能。最短30分で審査結果をご連絡します。'},
      {name: 'keywords', content: 'au PAY スマートローン,au,カードレスローン,スマートフォン,au PAY 残高,セブン銀行スマホATM,借入,返済,手続き,限度額'}
    ]
  },
  components: {
    BtnText,
    CardBody,
    CardImage,
    CardHeader,
    CardBox,
    CardGroups,
    CvBox,
    BtnMain,
    BtngroupSingle,
    ListItem,
    ListGroup,
    IconBox,
    ContentsCard,
    TalkBox,
    TalkGroup,
    HeadingTwo,
    ContentsIntro,
    ContentsHeader
  },
  data () {
    return {
      breadcrumbItems: [{
        text: 'トップ',
        routerName: 'root'
      },
      {
        text: '知っておきたい！ローンについて',
        routerName: 'sv003'
      },
      {
        text: 'いまさら聞けない！ローンの基礎知識',
        routerName: 'sv043'
      },
      {
        text: 'ローンって借りすぎないかな？',
        active: true
      }]
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../assets/stylesheets/styles";

.contents-box01 {
  display: flex;
  align-items: flex-start;
  @include mq-down(md) {
    flex-wrap: wrap;
  }
  >.talk-group {
    @include mq-up(md) {
      width: 50%;
    }
  }
  >.contents-card {
      @include mq-up(md) {
        width: 50%;
      }
    >.card-body {
      padding-bottom: 10px !important;
    }
  }
}

.contents-box02 {
  display: flex;
  @include mq-down(md) {
    flex-wrap: wrap;
  }
  >.contents-card {
    width: 100%;
    @include mq-up(md) {
      width: 50%;
    }
    >.card-body {
      padding-top: 30px;
      padding-bottom: 30px;
      >.card-groups {
        >.card-box {
          >.txt01 {
            font-size:14px;
            a {
              text-decoration: underline;
              word-break: break-all;
            }
          }
          >.txt02 {
            padding-top: 20px;
            padding-bottom: 20px;
            text-align: center;
            margin-bottom: 0;
            &.-border {
              border-top: 1px dotted $secondary;
              border-bottom: 1px dotted $secondary;
            }
            >.txt03 {
              font-size: 16px;
              font-weight: bold;
              display: block;
              @include mq-up(md) {
                font-size: 20px;
              }
            }
          }
        }
      }
    }
  }
}

.contents-card {
  >.card-body01 {
    background-color: #fff;
    border-radius: 10px;
    padding-top: 30px;
    padding-left: 20px;
    padding-right: 20px;
    padding-bottom: 30px;
    @include mq-up(md) {
      padding-left: 50px !important;
      padding-right: 50px !important;
    }
    >.txt01 {
      font-size: 12px;
      word-break:break-all;
      @include mq-up(md) {
        margin-bottom: 25px;
        font-size: 14px;
      }
    }
    >.card-groups {
      display: flex;
      @include mq-down(md) {
        flex-wrap: wrap;
      }
      @include mq-up(md) {
        justify-content: space-between;
        align-items: center;
      }
      >.card-box {
        @include mq-up(md) {
          width: 50%;
        }
        >.card-image {
          width: 80%;
          margin: 0 auto;
        }
        >.card-body {
          >.-border {
            border: 1px solid $secondary;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            padding: 20px 20px 0px 20px;
            border-radius: 10px;
            @include mq-up(md) {
              padding: 30px 40px 20px 40px;
            }
            .list {
              margin-bottom: 20px;
              @include mq-down(md) {
                font-size: 12px;
              }
              &.circle-box {
                width: 5%;
                >.circle {
                  width: 15px;
                  height: 15px;
                  color: #fff;
                  border-radius: 50%;
                  font-size: 11px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  float: left;
                  margin-right: 5px;
                  @include mq-up(md) {
                    width: 24px;
                    height: 24px;
                    font-size: 14px;
                    margin-right: 10px;
                  }
                  &.-green {
                    background-color: $green;
                  }
                  &.-orange {
                    background-color: $orange;
                  }
                  &.-blue {
                    background-color: $blue;
                  }
                }
              }
              &.txt01 {
                width: 45%;
                padding-left: 10px;
                @include mq-up(md) {
                  padding-left: 10px;
                }
              }
            }
          }
        }
      }
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/pages/Sv011.vue